/**
 * Prepara una imagen para subirla (logotipo): si ya es PNG, JPG o WebP y pesa poco, se sube tal cual; si pesa demasiado
 * o es de otro tipo (GIF, BMP, SVG…), se convierte y se reduce aquí mismo hasta que cabe. Si no se puede, el error dice por qué.
 */
const TIPOS = ["image/png", "image/jpeg", "image/webp"];

export const pesoLegible = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const leer = (b: Blob) => new Promise<string>((ok, mal) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result));
  r.onerror = () => mal(new Error("No se pudo leer el archivo. Vuelve a elegirlo."));
  r.readAsDataURL(b);
});

function abrir(archivo: File): Promise<HTMLImageElement> {
  return new Promise((ok, mal) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); ok(img); };
    img.onerror = () => { URL.revokeObjectURL(url); mal(new Error(`No se pudo abrir «${archivo.name}» como imagen. Usa un archivo PNG, JPG o WebP (si es una foto del celular en formato HEIC, guárdala como JPG).`)); };
    img.src = url;
  });
}

const nombreTipo = (a: File) => (a.type.split("/")[1] || a.name.split(".").pop() || "de otro tipo").replace("svg+xml", "SVG").toUpperCase();
const aBlob = (c: HTMLCanvasElement, tipo: string, calidad: number) => new Promise<Blob | null>((ok) => c.toBlob(ok, tipo, calidad));

export async function prepararLogo(archivo: File, maxBytes: number): Promise<{ dataUrl: string; aviso: string }> {
  if (!archivo.size) throw new Error(`El archivo «${archivo.name}» está vacío.`);
  if (TIPOS.includes(archivo.type) && archivo.size <= maxBytes) { await abrir(archivo); return { dataUrl: await leer(archivo), aviso: "" }; }

  const img = await abrir(archivo);
  const ancho = img.naturalWidth, alto = img.naturalHeight;
  if (!ancho || !alto) throw new Error(`«${archivo.name}» no tiene un tamaño que Donna pueda usar. Prueba con un PNG, JPG o WebP.`);
  // el logo se muestra pequeño: más de 1000 px no aporta nada. Se prueban lados cada vez menores hasta que cabe.
  for (const lado of [1000, 800, 600, 400, 300]) {
    const k = Math.min(1, lado / Math.max(ancho, alto));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(ancho * k)); c.height = Math.max(1, Math.round(alto * k));
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    for (const [tipo, calidad] of [["image/webp", 0.9], ["image/webp", 0.75], ["image/png", 1], ["image/jpeg", 0.85]] as const) {
      const b = await aBlob(c, tipo, calidad);
      if (b && TIPOS.includes(b.type) && b.size <= maxBytes) {
        const motivo = TIPOS.includes(archivo.type) ? `pesaba ${pesoLegible(archivo.size)}` : `era un archivo ${nombreTipo(archivo)}`;
        return { dataUrl: await leer(b), aviso: `El logo ${motivo} y se ajustó automáticamente a ${pesoLegible(b.size)} (${c.width}×${c.height} px).` };
      }
    }
  }
  throw new Error(`«${archivo.name}» pesa ${pesoLegible(archivo.size)} y no se pudo reducir a menos de ${pesoLegible(maxBytes)}. Prueba con una imagen más simple o exportada con menos detalle.`);
}
